import { useState } from 'react'

import { errorMessage } from '@/shared/api/httpClient'
import { useAsync } from '@/shared/api/useAsync'
import { Blueprint } from '@/shared/ui/Blueprint'
import { mcpApi } from '@/features/mcp/mcpApi'
import type { Connection } from '@/features/mcp/types'

// O console não inicia a vinculação: é o assistente que pede acesso via OAuth.
// Por isso a etapa 1 difere do protótipo ("Clique em conectar").
const ETAPAS = [
  { n: 1, titulo: 'Peça no assistente', texto: 'Adicione o Lumo como conector no assistente. Ele abre o consentimento aqui, com os escopos que você escolher.' },
  { n: 2, titulo: 'Autorize no consentimento', texto: 'Você confirma quais cômodos ele vê e se pode executar ações.' },
  { n: 3, titulo: 'Volta vinculado', texto: 'O código vira token no retorno. Nada de chave copiada à mão.' },
]

const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' })
function relative(iso: string) {
  const sec = (new Date(iso).getTime() - Date.now()) / 1000
  const abs = Math.abs(sec)
  if (abs < 60) return 'agora'
  if (abs < 3600) return rtf.format(Math.round(sec / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(sec / 3600), 'hour')
  return rtf.format(Math.round(sec / 86400), 'day')
}
const shortDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })

export function McpPage() {
  const { data, setData, error, loading } = useAsync(async () => {
    const [connections, info] = await Promise.all([mcpApi.connections(), mcpApi.info()])
    return { connections, info }
  })
  const [actionError, setActionError] = useState<string | null>(null)

  if (loading) return <p className="page-state">Carregando…</p>
  if (!data) return <p className="page-state">{error}</p>

  const { connections, info } = data
  const summary = connections.length
    ? `${connections.map((c) => c.client_name).join(', ')} ${connections.length === 1 ? 'está vinculado' : 'estão vinculados'}`
    : 'nenhum assistente vinculado ainda'

  async function revoke(c: Connection) {
    setActionError(null)
    try {
      await mcpApi.revoke(c.id)
      setData((d) => d && { ...d, connections: d.connections.filter((x) => x.id !== c.id) })
    } catch (err) {
      setActionError(errorMessage(err))
    }
  }

  return (
    <div>
      <h2 style={{ margin: 0 }}>Conexão MCP</h2>
      <p className="text-muted" style={{ fontSize: 14, maxWidth: '74ch' }}>
        Vincular é como entrar com o Google: o assistente pede acesso, você autoriza no consentimento e ele volta vinculado. Nenhuma chave para copiar e colar.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,200px),1fr))', gap: 18, marginTop: 24 }}>
        {ETAPAS.map((et) => (
          <Blueprint key={et.n} style={{ padding: 16 }}>
            <span className="tag tag-neutral" style={{ minWidth: 26, justifyContent: 'center' }}>{et.n}</span>
            <div className="heading" style={{ fontSize: 17, marginTop: 10 }}>{et.titulo}</div>
            <div className="text-muted" style={{ fontSize: 13 }}>{et.texto}</div>
          </Blueprint>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '34px 0 14px' }}>
        <h6 style={{ margin: 0, color: 'var(--color-accent-700)' }}>Assistentes</h6>
        <span className="text-muted" style={{ fontSize: 13 }}>{summary}</span>
      </div>
      {actionError && <p className="form-error" style={{ marginBottom: 12 }}>{actionError}</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {connections.map((c) => (
          <Blueprint key={c.id} style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 230 }}>
              <div className="heading" style={{ fontSize: 19 }}>{c.client_name}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                vinculado em {shortDate(c.created_at)} · último acesso {c.last_used_at ? relative(c.last_used_at) : '—'}
              </div>
            </div>
            <span className="tag tag-accent">vinculado</span>
            <span className="text-muted" style={{ fontSize: 13 }}>
              {c.can_execute ? 'pode ler e agir' : 'somente leitura'} · {c.rooms.length ? c.rooms.map((r) => r.name).join(', ') : 'toda a casa'}
            </span>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn btn-secondary" onClick={() => revoke(c)}>
              Revogar acesso
            </button>
          </Blueprint>
        ))}
      </div>

      <Blueprint style={{ padding: 18, marginTop: 26 }}>
        <h6 style={{ margin: '0 0 8px', color: 'var(--color-accent-700)' }}>Como vincular um assistente</h6>
        <p style={{ fontSize: 14, margin: 0, maxWidth: '74ch' }}>
          No Claude, adicione um conector personalizado com a URL <span className="mono">{info.mcp_url}</span>. Na primeira conexão, a tela de
          consentimento abre aqui no Lumo — você escolhe os cômodos e se ele pode agir. O assistente aparece nesta lista assim que autorizado.
        </p>
      </Blueprint>

      <Blueprint style={{ padding: 18, marginTop: 26 }}>
        <h6 style={{ margin: '0 0 12px', color: 'var(--color-accent-700)' }}>Endpoints do servidor</h6>
        <div className="mono" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,240px),1fr))', gap: 14, fontSize: 12, overflowWrap: 'anywhere' }}>
          {[
            ['MCP', info.mcp_url],
            ['Authorize', info.authorize_url],
            ['Token', info.token_url],
          ].map(([label, url]) => (
            <div key={label}>
              <div className="kicker" style={{ fontFamily: 'var(--font-body)' }}>{label}</div>
              {url}
            </div>
          ))}
        </div>
        <div className="text-muted" style={{ fontSize: 12, marginTop: 12 }}>
          OAuth 2.1 com PKCE · escopos <span className="mono">devices.read</span> e <span className="mono">devices.execute</span> · revogar aqui invalida o refresh token na hora.
        </div>
      </Blueprint>
    </div>
  )
}
